/**
 * All user-visible numbers, money, percentages and dates go through these helpers
 * (CLAUDE.md §6.9). Intl formatters are cached per locale and options. Never call toFixed in UI.
 */
import { INTL_LOCALE, type Locale } from './locale';

export type Numeric = string | number;

const numberCache = new Map<string, Intl.NumberFormat>();
const dateCache = new Map<string, Intl.DateTimeFormat>();

function nf(locale: Locale, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = numberCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(INTL_LOCALE[locale], options);
    numberCache.set(key, fmt);
  }
  return fmt;
}

function df(locale: Locale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = dateCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(INTL_LOCALE[locale], options);
    dateCache.set(key, fmt);
  }
  return fmt;
}

export function toNumber(value: Numeric): number {
  return typeof value === 'number' ? value : Number(value);
}

/** The true minus sign (U+2212) instead of a hyphen. */
function minus(text: string): string {
  return text.replace(/-/g, '−');
}

export interface MoneyOptions {
  /** Decimal places; 2 for amounts, the instrument's tick decimals for prices. */
  places?: number;
  signed?: boolean;
}

export function formatMoney(value: Numeric, locale: Locale, options: MoneyOptions = {}): string {
  const places = options.places ?? 2;
  return minus(
    nf(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: places,
      maximumFractionDigits: places,
      signDisplay: options.signed === true ? 'exceptZero' : 'auto',
    }).format(toNumber(value)),
  );
}

export function formatNumber(value: Numeric, locale: Locale, places = 0): string {
  return minus(
    nf(locale, { minimumFractionDigits: 0, maximumFractionDigits: places }).format(toNumber(value)),
  );
}

/** A price with a fixed number of decimals and no currency (indexes, FX and other markets). */
export function formatPrice(value: Numeric, locale: Locale, places = 2): string {
  return minus(
    nf(locale, { minimumFractionDigits: places, maximumFractionDigits: places }).format(
      toNumber(value),
    ),
  );
}

/** Large counts in short form: 1.2M, 340K. */
export function formatCompact(value: Numeric, locale: Locale): string {
  return nf(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(toNumber(value));
}

/** Share counts: whole numbers stay whole, fractional ones show up to 4 decimals. */
export function formatShares(value: Numeric, locale: Locale): string {
  return formatNumber(value, locale, 4);
}

/** `value` is in percent units (2.4 → "2.4%"). */
export function formatPercent(
  value: Numeric,
  locale: Locale,
  options: { places?: number; signed?: boolean } = {},
): string {
  const places = options.places ?? 1;
  return minus(
    nf(locale, {
      style: 'percent',
      minimumFractionDigits: places,
      maximumFractionDigits: places,
      signDisplay: options.signed === true ? 'exceptZero' : 'auto',
    }).format(toNumber(value) / 100),
  );
}

/** A ratio (0.153 → "15.3%"). */
export function formatRatio(
  value: number,
  locale: Locale,
  options: { places?: number; signed?: boolean } = {},
): string {
  return formatPercent(value * 100, locale, options);
}

export function placesOf(tick: string): number {
  const decimals = tick.includes('.') ? (tick.split('.')[1] ?? '').replace(/0+$/, '').length : 0;
  return Math.max(2, decimals);
}

export function formatDate(value: string | Date, locale: Locale, timeZone?: string): string {
  const date = typeof value === 'string' ? parseDate(value) : value;
  return df(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

export function formatDateTime(value: string | Date, locale: Locale, timeZone?: string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return df(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

export function formatTime(value: string | Date, locale: Locale, timeZone?: string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return df(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

export function formatMonth(value: Date, locale: Locale): string {
  return df(locale, { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(value);
}

/** "2026-10-05" (a calendar day) → a UTC midnight Date, so time zones never shift the day. */
export function parseDate(day: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? new Date(`${day}T00:00:00Z`) : new Date(day);
}

export function formatRelative(value: string | Date, now: Date, locale: Locale): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(INTL_LOCALE[locale], { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.trunc(seconds / size), unit);
  }
  return rtf.format(0, 'minute');
}

export type ChartTimeKind = 'year' | 'month' | 'day' | 'time' | 'dayTime' | 'fullDay';

/**
 * Chart axis and crosshair labels. Chart times are wall-clock times encoded as UTC (the chart kit
 * shifts them into the owner's zone), so they are formatted in UTC.
 */
export function formatChartTime(value: Date, kind: ChartTimeKind, locale: Locale): string {
  const options: Record<ChartTimeKind, Intl.DateTimeFormatOptions> = {
    year: { year: 'numeric' },
    month: { month: 'short' },
    day: { day: 'numeric', month: 'short' },
    time: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
    dayTime: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    },
    fullDay: { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' },
  };
  const text = df(locale, { ...options[kind], timeZone: 'UTC' }).format(value);
  // Some languages (Bulgarian) write a lone short month as a number ("09"); name it instead.
  return kind === 'month' && /^\d+$/.test(text)
    ? df(locale, { month: 'long', timeZone: 'UTC' }).format(value)
    : text;
}

/** Minutes to add to UTC for wall-clock time in `timeZone` at `when` (e.g. +120 in Berlin summer). */
export function zoneOffsetMinutes(when: Date, timeZone: string): number {
  const parts = df('en', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(when);
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const wall = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return Math.round((wall - Math.floor(when.getTime() / 1000) * 1000) / 60000);
}
